import isEqual from 'lodash/isEqual';
import memoize from 'memoize-one';
import type { ComponentProps, FC, ReactNode } from 'react';
import React, { memo, useContext, useEffect, useState } from 'react';
import type { GraphQLTaggedNode } from 'react-relay';
import { graphql, useFragment } from 'react-relay';

import type { EventHandlers } from '@atlaskit/editor-common/EventHandlers';
import type { RendererAppearance, StickyHeaderProps } from '@atlaskit/renderer/types';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type { DocNode } from '@atlaskit/adf-schema/doc';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import type { ApolloInteropMutableHelper } from '@confluence/apollo-relay-interop/entry-points/ApolloInteropMutableHelper.ts';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils.ts';
import type { ContentRendererQueryVariablesType as VariablesType } from '@confluence/content-body/entry-points/ContentRendererQuery.ts';
import { getContentAppearanceForAtlaskit } from '@confluence/content-appearance/entry-points/atlaskitTransformations.ts';
import { getContentAppearancePublished } from '@confluence/content-appearance/entry-points/contentAppearance.ts';
import { removeJquery } from '@confluence/dom-helpers';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import { useInlineHighlights } from '@confluence/inline-highlights-query';
import {
	globalMacrosCSSRecorder,
	globalMacrosJSRecorder,
} from '@confluence/ssr-utilities/entry-points/ResourceRecorder.ts';
import {
	superbatchCSSRecorder,
	superbatchJSRecorder,
} from '@confluence/ssr-utilities/entry-points/SuperbatchResourceRecorder.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { useSpaceId } from '@confluence/space-utils/entry-points/useSpaceId.ts';
import { RendererExtensionContext } from '@confluence/content-renderer-extension-context/entry-points/RendererExtensionContext.ts';
import {
	scopeCSS,
	extractCSS,
} from '@confluence/content-renderer-legacy-macros/entry-points/scopeStyleMacro.ts';
import { adfToPlainText } from '@confluence/experiment-meeting-signal-core/entry-points/adfToPlainText.ts';
import { useMeetingSignalDetection } from '@confluence/experiment-meeting-signal-instrumentation/entry-points/useMeetingSignalDetection.ts';
import { isMeetingSignalAnalysisEnabled } from '@confluence/experiment-meeting-signal/entry-points/isMeetingSignalAnalysisEnabled.ts';
import { useMeetingSignalExperiment } from '@confluence/experiment-meeting-signal/entry-points/useMeetingSignalExperiment.ts';
import { useMeetingSignalReminder } from '@confluence/experiment-meeting-signal/entry-points/useMeetingSignalReminder.ts';
import { type WithFlagsProps, withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { ContentModeFragmentProvider } from '@confluence/editor-content-mode-button/entry-points/ContentModeFragmentProvider.ts';

import type { ContentBodyType, ContentDataType, ContentNodeType } from './__types__/index.ts';
import { RendererPerformanceMetrics } from './RendererPerformanceMetrics.tsx';
import { useConfluencePreviewsForceLoad } from './useConfluencePreviewsForceLoad.tsx';
import { PageContentRendererTiny, PageContentRendererFabric } from './renderers/index.ts';
import { useGloballyDefinedMacroResources } from './useGloballyDefinedMacroResources.tsx';
import type { PageContentRenderer_content$key } from './__generated__/PageContentRenderer_content.graphql.ts';
import type { PageContentRenderer_space$key } from './__generated__/PageContentRenderer_space.graphql.ts';

export const PageContentRenderer_content: GraphQLTaggedNode = graphql`
	fragment PageContentRenderer_content on Content {
		id
		title
		type
		status
		version {
			confRev
			number
		}
		metadata {
			lastModifiedDate
		}
		body {
			dynamic {
				representation
				value
				content {
					id
					version {
						number
					}
				}
				webresource {
					superbatch {
						tags {
							js
							css
						}
					}
					tags {
						js
						css
					}
				}
			}
		}
		appearancePublished: properties(key: "content-appearance-published") {
			nodes {
				id
				key
				value
				version {
					number
				}
			}
		}
		properties(
			keys: [
				"cover-picture-id-published"
				"emoji-title-published"
				"editor"
				"import-source"
				"imported-page-modified-date"
			]
		) {
			nodes {
				id
				key
				value
				version {
					number
				}
			}
		}
	}
`;

export const PageContentRenderer_space: GraphQLTaggedNode = graphql`
	fragment PageContentRenderer_space on Space {
		id
		theme {
			themeKey
		}
	}
`;

const MeetingSignalPageLoadReminder = withFlags(
	({ contentId, flags }: WithFlagsProps & { contentId: string }): null => {
		const { consumeReminder, isEnabled: isMeetingSignalReminderEnabled } = useMeetingSignalReminder(
			{ consumptionBoundary: 'pageLoad', contentId, flags },
		);

		useEffect(() => {
			if (isMeetingSignalReminderEnabled) {
				consumeReminder();
			}
		}, [consumeReminder, isMeetingSignalReminderEnabled]);

		return null;
	},
);

export interface ContentRendererProps extends VariablesType {
	allowStickyHeaders?: boolean;
	appearance?: RendererAppearance;
	contentId?: string;
	data?: ContentDataType;
	contentFragmentRef?: PageContentRenderer_content$key | null;
	contentModeFragmentRef?: ComponentProps<typeof ContentModeFragmentProvider>['contentFragmentRef'];
	spaceFragmentRef?: PageContentRenderer_space$key | null;
	error?: Error;
	eventHandlerOverrides?: Partial<EventHandlers>;
	hasInlineComments?: boolean;
	preloadedCanAddComments?: boolean;
	hasProgressiveRendering?: boolean;
	isPreviewMode?: boolean;
	isPresentational?: boolean;
	isSpaceOverview?: boolean;
	isTemplateEditorRenderer?: boolean;
	onRendered?: () => void;
	queryHash?: string;
	spaceKey?: string | null;
	stickyHeaders?: StickyHeaderProps;
	isArchived?: boolean;
	contentStatusError?: ApolloErrorInterop;
	isEmbeddedPage?: boolean;
	customPerfData?: Record<string, string | number | boolean | object>;
	// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
	document?: DocNode;
}

const renderPerformanceMetrics = memoize(
	(props) => () => <RendererPerformanceMetrics {...props} />,
	isEqual,
);

/**
 * Record superbatch to be included into HTML on the server
 * @param webresource -- superbatch CSS/JS links from monolith
 */
const recordSuperbatch = (webresource: any) => {
	const superbatchJs = webresource?.superbatch?.tags?.js || '';
	const superbatchCss = webresource?.superbatch?.tags?.css || '';

	if (superbatchJs) {
		superbatchJSRecorder.includeSuperBatchResources();
		if (!superbatchJSRecorder.get()) {
			superbatchJSRecorder.add(removeJquery(superbatchJs));
		}
	}

	if (superbatchCss) {
		superbatchCSSRecorder.includeSuperBatchResources();
		if (!superbatchCSSRecorder.get()) {
			superbatchCSSRecorder.add(superbatchCss);
		}
	}
};

/**
 * Record monolith-defined JS and CSS files for macros
 * @param webresource -- tags CSS/JS links from monolith
 */
const recordGlobalJsAndCSS = (webresource: any) => {
	const js = webresource?.tags?.js || '';
	const css = webresource?.tags?.css || '';
	if (!globalMacrosCSSRecorder.get()) {
		globalMacrosCSSRecorder.add(css);
	}
	if (!globalMacrosJSRecorder.get()) {
		globalMacrosJSRecorder.addDeferTags(js);
	}
};

export const includeSuperbatchForFabricMacro = (
	contentBody: any,
	macroRenderedOutput: any,
): void => {
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (!process.env.REACT_SSR) {
		return;
	}

	recordSuperbatch(contentBody?.webresource);

	/* to avoid type errors */
	if (macroRenderedOutput && macroRenderedOutput.length > 0) {
		window['__LEGACY_MACRO_RENDERED_OUTPUT__'] = macroRenderedOutput;
		window['__SSR_MACROS_INFO__'] = {
			successes: {},
			failures: {},
			successCount: 0,
			failureCount: 0,
		};
	}
};

const DEFAULT_EVENT_HANDLER_OVERRIDES = {};

type PageContentRendererContentProps = Omit<
	ContentRendererProps,
	'data' | 'contentFragmentRef' | 'spaceFragmentRef'
> & {
	content: ContentNodeType;
	hasTheme: boolean;
	hasData: boolean;
};

/**
 * Component that renders content for Confluence Page
 * WARN: it must be renamed to PageContentRenderer to avoid confusion
 *
 * This uses the AtlasKit renderer component for rendering, and configures the
 * renderer to handle Confluence content (media, macros, etc.).
 *
 * The difference between this and `ContentRenderer` is that this utilizes the
 * dynamic content query, while `ContentRenderer` attempts to fetch ADF. This
 * an experiment and will be removed when finished.
 */
const PageContentRendererContent: FC<PageContentRendererContentProps> = memo(
	({
		appearance,
		contentId,
		content,
		contentModeFragmentRef,
		hasTheme,
		hasData,
		error,
		eventHandlerOverrides = DEFAULT_EVENT_HANDLER_OVERRIDES,
		hasInlineComments = true,
		isPreviewMode,
		isSpaceOverview = false,
		isTemplateEditorRenderer,
		queryHash,
		spaceKey,
		isArchived,
		contentStatusError,
		isEmbeddedPage,
		customPerfData,
		document,
		...rest
	}) => {
		const contentBody: ContentBodyType = content?.body?.dynamic || null;
		const isFabric = contentBody?.representation === 'atlas_doc_format';
		const webresource = contentBody?.webresource;

		/* useInlineHighlights is custom hook that queries for markerRefs only if Inline Highlights performance is Enabled */
		const { markerRefs } = useInlineHighlights({
			pageId: contentId as string,
			shouldFireQuery:
				!isFabric &&
				// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
				Boolean(process.env.REACT_SSR) &&
				hasInlineComments, // fabric pages don't need to fire query for markerRefs
		});
		const { getLegacyContentExtensions } = useContext(RendererExtensionContext);
		const [rootEl, setRootEl] = useState<HTMLDivElement | null>(null);

		const { createAnalyticsEvent } = useAnalyticsEvents();
		const spaceId = useSpaceId();
		const detectMeetingSignal = useMeetingSignalDetection();
		const { evaluate: evaluateMeetingSignalExperiment } = useMeetingSignalExperiment();
		const shouldAnalyzeMeetingSignal = isMeetingSignalAnalysisEnabled();

		// Meeting-signal instrumentation (STARB-3763)
		useEffect(() => {
			if (!shouldAnalyzeMeetingSignal || isPreviewMode || isTemplateEditorRenderer || !contentId) {
				return;
			}
			const signal = {
				pageType: 'page',
				trigger: 'load',
				titleText: content?.title,
				contentText: isFabric ? adfToPlainText(contentBody?.value) : undefined,
			} as const;
			detectMeetingSignal(signal);
			evaluateMeetingSignalExperiment(signal);
		}, [
			detectMeetingSignal,
			evaluateMeetingSignalExperiment,
			isPreviewMode,
			isTemplateEditorRenderer,
			contentId,
			content?.title,
			contentBody?.value,
			isFabric,
			shouldAnalyzeMeetingSignal,
		]);

		useGloballyDefinedMacroResources({
			contentBody,
		});
		useConfluencePreviewsForceLoad(contentId);

		if (error) {
			return <ErrorDisplay error={error} />;
		}

		// Bail out before rendering when we have no content to render: either there's no contentId,
		// or the caller passed an in-memory `document` but its backing data hasn't arrived yet.
		// We can't exit earlier because hooks can't be conditional.
		if (!contentId || (!hasData && Boolean(document))) {
			return null;
		}

		// On the server the content query result can occasionally be absent from the store at the
		// moment this component renders (a rare SSR data-availability gap, sometimes coinciding with
		// transient backend errors on sibling queries). When that happens `content` is null and we
		// would otherwise commit an *empty* legacy TinyMCE renderer into the SSR HTML for what is
		// really a Fabric page (a silent blank page that hydrates against the wrong renderer).
		// Instead, render nothing on the server and report an SSR error, so the client can
		// hydrate/refetch and render the correct content. Scoped to the query-driven path
		// (no in-memory `document`).
		if (
			// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
			process.env.REACT_SSR &&
			!document &&
			!content
		) {
			// On the server getMonitoringClient() returns the SSRMonitoringClient, so this is
			// collected and reported through the SSR error pipeline (SSR_ERROR).
			getMonitoringClient().submitError(
				new Error(
					`PageContentRenderer: content data was unavailable at SSR render time for contentId=${contentId}; rendering null so the client can hydrate/refetch instead of committing an empty legacy renderer`,
				),
				{ attribution: Attribution.BACKBONE },
			);
			return null;
		}

		// With https://app.launchdarkly.com/confluence/staging/features/confluence.macro.execution.adf.extension.macroNames/targeting
		// the output is not put into macroRenderedOutput, and is included on top level.
		// We use the resourceRecorder only in SSR environment.
		const revision = content?.version?.confRev || '';
		const lastModifiedDate = content?.metadata?.lastModifiedDate;

		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		if (process.env.REACT_SSR && isFabric && (webresource?.tags?.js || webresource?.tags?.css)) {
			recordSuperbatch(webresource);
			recordGlobalJsAndCSS(webresource);
		}

		let contentAppearance = appearance;
		if (!contentAppearance && content) {
			contentAppearance = getContentAppearanceForAtlaskit(
				getContentAppearancePublished(content).appearance,
			);
		}

		const isCompanyHub = isCompanyHubSpaceKey(spaceKey);
		if (isCompanyHub) {
			// Company Hub editors no longer have the option of changing page width,
			// as content topper is removed. For users who had previously set Company
			// Hub page to 'full-width' and can no longer change it back, we force it to
			// 'full-page' to fix unexpected layout issues.
			contentAppearance = 'full-page';
		}

		window.performance.mark('CFP-63.next.content-renderer.rendered');
		const meetingSignalPageLoadReminder =
			!isPreviewMode && !isTemplateEditorRenderer ? (
				<MeetingSignalPageLoadReminder contentId={contentId} />
			) : null;
		const renderWithMeetingSignalPageLoadReminder = (renderer: ReactNode) => (
			<>
				{meetingSignalPageLoadReminder}
				{renderer}
			</>
		);
		const renderWithContentModeFragment = (renderer: ReactNode) =>
			contentModeFragmentRef ? (
				<ContentModeFragmentProvider contentFragmentRef={contentModeFragmentRef}>
					{renderer}
				</ContentModeFragmentProvider>
			) : (
				renderer
			);

		if (isFabric) {
			return renderWithMeetingSignalPageLoadReminder(
				renderWithContentModeFragment(
					<PageContentRendererFabric
						content={content}
						contentAppearance={contentAppearance}
						contentBody={contentBody}
						contentId={contentId}
						eventHandlerOverrides={eventHandlerOverrides}
						hasInlineComments={hasInlineComments}
						isPreviewMode={isPreviewMode}
						isSpaceOverview={isSpaceOverview}
						isTemplateEditorRenderer={isTemplateEditorRenderer}
						markerRefs={markerRefs}
						queryHash={queryHash}
						renderPerformanceMetrics={renderPerformanceMetrics}
						revision={revision}
						rootEl={rootEl}
						setRootEl={setRootEl}
						spaceKey={spaceKey}
						spaceId={spaceId}
						isArchived={isArchived}
						contentStatusError={contentStatusError}
						isEmbeddedPage={isEmbeddedPage}
						//this is to correct a bug in nav4 where sticky table headers are incorrectly offset in nav4
						//this should also be false in Nav3 however:
						//the issue is that bannerState is incorrectly reporting a height of 0px in preview mode because it uses WithoutBanners because I guess at one point it didn't have top nav
						//the hardcoded isContentHeaderFixedAtTop and the banner state hardcoded top nav height happen to be 1px different and this failure just happened to have worked all this time.
						//TODO:in nav4 remove bannerContext entirely and simply offset the scroll container naturally to remove these complicated calculations
						isContentHeaderFixedAtTop={!hasTheme && !isEmbeddedPage && !isPreviewMode}
						customPerfData={customPerfData}
						document={document}
						{...rest}
					/>,
				),
			);
		}

		// Scope Style Macros CSS for Tiny Page
		try {
			const { CSSForAnalytics, contentWithScopedCSS } = scopeCSS(contentBody?.value || '');

			// The component can load multiple times, contentWithScopedCSS with the same contentBody.value to prevent sending multiple events
			if (
				CSSForAnalytics &&
				// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
				!process.env.REACT_SSR &&
				contentWithScopedCSS !== contentBody?.value
			) {
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'scoped',
						actionSubject: 'legacyStyleMacro',
						source: 'PageContentRenderer',
						attributes: {
							CSSForAnalytics,
						},
					},
				}).fire();
			}

			if (contentBody) {
				(contentBody as ApolloInteropMutableHelper<NonNullable<typeof contentBody>>).value =
					contentWithScopedCSS;
			}
		} catch (error) {
			const extractedCSS = extractCSS(contentBody?.value || '');
			// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
			if (process.env.REACT_SSR) {
				getMonitoringClient().submitError(
					{ error, extractedCSS },
					{ attribution: Attribution.BACKBONE },
				);
			} else {
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'error',
						actionSubject: 'legacyStyleMacro',
						source: 'PageContentRenderer',
						attributes: {
							error,
							extractedCSS,
						},
					},
				}).fire();
			}
		}

		return renderWithMeetingSignalPageLoadReminder(
			<PageContentRendererTiny
				contentAppearance={contentAppearance}
				contentBody={contentBody}
				contentId={contentId}
				spaceKey={spaceKey}
				getLegacyContentExtensions={getLegacyContentExtensions}
				markerRefs={markerRefs}
				queryHash={queryHash}
				renderPerformanceMetrics={renderPerformanceMetrics}
				revision={revision}
				setRootEl={setRootEl}
				lastModifiedDate={lastModifiedDate}
			/>,
		);
	},
);

const PageContentRendererWithData: FC<ContentRendererProps> = memo(
	({
		data,
		contentFragmentRef: _contentFragmentRef,
		spaceFragmentRef: _spaceFragmentRef,
		...props
	}) => {
		// need to check space hasTheme to determine whether the page header is always fixed at the top
		const hasTheme = Boolean(data?.space?.theme?.themeKey);
		const content: ContentNodeType = data?.content?.nodes?.[0] || null;

		return (
			<PageContentRendererContent
				{...props}
				content={content}
				hasTheme={hasTheme}
				hasData={Boolean(data)}
			/>
		);
	},
);

const PageContentRendererWithFragmentRefs: FC<ContentRendererProps> = memo(
	({ contentFragmentRef, spaceFragmentRef, ...props }) => {
		const content = useFragment(PageContentRenderer_content, contentFragmentRef ?? null) ?? null;
		const space = useFragment(PageContentRenderer_space, spaceFragmentRef ?? null);

		return (
			<PageContentRendererContent
				{...props}
				content={content}
				hasTheme={Boolean(space?.theme?.themeKey)}
				hasData={Boolean(contentFragmentRef)}
			/>
		);
	},
);

const PageContentRendererComponent: FC<ContentRendererProps> = (props) => {
	if (props.contentFragmentRef) {
		return <PageContentRendererWithFragmentRefs {...props} />;
	}

	return <PageContentRendererWithData {...props} />;
};

const PageContentRenderer: FC<ContentRendererProps> = withErrorBoundary({
	attribution: Attribution.BACKBONE,
})(PageContentRendererComponent);
PageContentRenderer.displayName = 'PageContentRenderer';

export { PageContentRenderer };
